import { ref, onMounted, onUnmounted } from 'vue';

export type PerformanceLevel = 'high' | 'medium' | 'low';

export interface PerformanceMonitorOptions {
  /** FPS threshold for "low" performance (default: 30) */
  lowThreshold?: number;
  /** FPS threshold for "medium" performance (default: 45) */
  mediumThreshold?: number;
  /** Number of consecutive low samples before flagging (default: 3) */
  sustainedThreshold?: number;
  /** Whether to set --performance-level CSS property on <html> (default: true) */
  setCSSProperty?: boolean;
}

export interface PerformanceMonitorReturn {
  currentFPS: ReturnType<typeof ref<number>>;
  performanceLevel: ReturnType<typeof ref<PerformanceLevel>>;
  isLowPerformance: ReturnType<typeof ref<boolean>>;
  start: () => void;
  stop: () => void;
}

export function usePerformanceMonitor(
  options?: PerformanceMonitorOptions
): PerformanceMonitorReturn {
  const {
    lowThreshold = 30,
    mediumThreshold = 45,
    sustainedThreshold = 3,
    setCSSProperty = true,
  } = options ?? {};

  const currentFPS = ref(60);
  const performanceLevel = ref<PerformanceLevel>('high');
  const isLowPerformance = ref(false);

  let rafId: number | null = null;
  let frameCount = 0;
  let sampleStart = 0; // 0 = sentinel for "not yet started"
  let consecutiveLowSamples = 0;

  function updateLevel(fps: number): void {
    let level: PerformanceLevel;
    if (fps < lowThreshold) {
      level = 'low';
      consecutiveLowSamples++;
    } else if (fps < mediumThreshold) {
      level = 'medium';
      consecutiveLowSamples = 0;
    } else {
      level = 'high';
      consecutiveLowSamples = 0;
    }

    currentFPS.value = Math.round(fps);
    performanceLevel.value = level;
    isLowPerformance.value = consecutiveLowSamples >= sustainedThreshold;

    if (setCSSProperty && typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--performance-level', level);
    }
  }

  function tick(timestamp: number): void {
    if (!sampleStart) {
      sampleStart = timestamp;
      frameCount = 0;
    }

    frameCount++;

    const elapsed = timestamp - sampleStart;
    if (elapsed >= 1000) {
      const fps = (frameCount * 1000) / elapsed;
      updateLevel(fps);
      sampleStart = timestamp;
      frameCount = 0;
    }

    rafId = requestAnimationFrame(tick);
  }

  function start(): void {
    if (rafId !== null) return;
    sampleStart = 0;
    frameCount = 0;
    consecutiveLowSamples = 0;
    rafId = requestAnimationFrame(tick);
  }

  function stop(): void {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  onMounted(start);
  onUnmounted(stop);

  return {
    currentFPS,
    performanceLevel,
    isLowPerformance,
    start,
    stop,
  };
}
